import { MarketingAttributionEvent } from "@redotech/redo-marketing-templates-common/marketing";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoLineAndBarChart } from "@redotech/redo-web/arbiter-components/charts/line-and-bar-chart/redo-line-and-bar-chart";
import {
  ChartSeries,
  DataPoint,
} from "@redotech/redo-web/arbiter-components/charts/line-and-bar-chart/types";
import { RedoMetricItem } from "@redotech/redo-web/arbiter-components/metric-item/redo-metric-item";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { getVisualizationColors } from "@redotech/redo-web/theme/colors";
import { memo, useMemo } from "react";
import {
  ChannelFilter,
  getChannelsForFilter,
} from "../filters/marketing-channel-filter";

interface DailyMessageMetricRow {
  DATE: Date;
  EMAIL_SENT: number;
  SMS_SENT: number;
  TOTAL_SENT: number;
}

export const MarketingAnalyticsMessagePerformance = memo(
  function MarketingAnalyticsMessagePerformance({
    dateFilter,
    attributionDays,
    channelFilter,
    attributionEvents,
  }: {
    dateFilter: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
    attributionDays: number;
    channelFilter: ChannelFilter;
    attributionEvents: MarketingAttributionEvent[];
  }) {
    const { startDate, endDate } = useMemo(() => {
      if (!dateFilter) {
        // Default to last 30 days if no valid date range
        const now = new Date();
        const thirtyDaysAgo = new Date(
          now.getTime() - 30 * 24 * 60 * 60 * 1000,
        );
        return {
          startDate: Temporal.Instant.from(thirtyDaysAgo.toISOString()),
          endDate: Temporal.Instant.from(now.toISOString()),
        };
      }
      return {
        startDate: Temporal.Instant.from(
          new Date(dateFilter[0].toString()).toISOString(),
        ),
        endDate: Temporal.Instant.from(
          new Date(dateFilter[1].toString()).toISOString(),
        ),
      };
    }, [dateFilter]);

    const {
      data: messageMetricsResult,
      isLoading,
      error: failedToLoad,
    } = useRpcQuery(
      RedoMarketingRpcClientContext,
      "getMessagePerformanceMetrics",
      {
        startDate,
        endDate,
        attributionDays,
        channels: getChannelsForFilter(channelFilter),
        attributionEvents,
      },
    );

    const calculatePercentageChange = (current: number, previous: number) => {
      if (previous === 0 && current === 0) {
        return 0;
      }
      if (previous === 0) {
        return null;
      }

      const change = ((current - previous) / previous) * 100;

      // Cap extremely large percentage changes for display purposes
      if (change > 999) {
        return 999;
      }
      if (change < -999) {
        return -999;
      }

      return change;
    };

    const totals = messageMetricsResult?.totals;

    const messagesSentChange = totals
      ? calculatePercentageChange(
          totals.totalMessagesSent,
          totals.previousTotalMessagesSent,
        )
      : 0;
    const openRateChange = totals
      ? calculatePercentageChange(totals.openRate, totals.previousOpenRate)
      : 0;
    const clickRateChange = totals
      ? calculatePercentageChange(totals.clickRate, totals.previousClickRate)
      : 0;
    const conversionRateChange = totals
      ? calculatePercentageChange(
          totals.conversionRate,
          totals.previousConversionRate,
        )
      : 0;

    const chartSeries = useMemo((): ChartSeries[] => {
      if (!messageMetricsResult?.dailyMetrics) {
        return [];
      }

      const emailData: DataPoint[] = messageMetricsResult.dailyMetrics.map(
        (metric: DailyMessageMetricRow) => ({
          date: Temporal.Instant.from(new Date(metric.DATE).toISOString()),
          value: metric.EMAIL_SENT,
        }),
      );

      const smsData: DataPoint[] = messageMetricsResult.dailyMetrics.map(
        (metric: DailyMessageMetricRow) => ({
          date: Temporal.Instant.from(new Date(metric.DATE).toISOString()),
          value: metric.SMS_SENT,
        }),
      );

      const colors = getVisualizationColors(2);

      return [
        { name: "Email", data: emailData, color: colors[0] },
        { name: "SMS", data: smsData, color: colors[1] },
      ];
    }, [messageMetricsResult?.dailyMetrics]);

    const formatPercentage = (value: number) => {
      return (value / 100).toLocaleString(undefined, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
        style: "percent",
      });
    };

    if (failedToLoad) {
      return (
        <Flex dir="column" gap="4xl" w="full">
          <Text fontSize="md" fontWeight="semibold">
            Message performance
          </Text>
          <Text color="red.500">Error loading message performance data</Text>
        </Flex>
      );
    }

    return (
      <Flex dir="column" gap="xl" w="full">
        <Text fontSize="md" fontWeight="semibold">
          Message performance
        </Text>

        {/* Metric Cards */}
        <Flex dir="row" gap="4xl" w="full">
          <RedoMetricItem
            pending={isLoading}
            title="Total messages sent"
            value={totals?.totalMessagesSent.toLocaleString() ?? "0"}
            {...(messagesSentChange !== null && {
              comparisonChange: messagesSentChange,
              comparisonText: "vs previous period",
              isGoodChange: messagesSentChange >= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Open rate"
            value={formatPercentage(totals?.openRate ?? 0)}
            {...(openRateChange !== null && {
              comparisonChange: openRateChange,
              comparisonText: "vs previous period",
              isGoodChange: openRateChange >= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Click rate"
            value={formatPercentage(totals?.clickRate ?? 0)}
            {...(clickRateChange !== null && {
              comparisonChange: clickRateChange,
              comparisonText: "vs previous period",
              isGoodChange: clickRateChange >= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Conversion rate"
            value={formatPercentage(totals?.conversionRate ?? 0)}
            {...(conversionRateChange !== null && {
              comparisonChange: conversionRateChange,
              comparisonText: "vs previous period",
              isGoodChange: conversionRateChange >= 0,
            })}
          />
        </Flex>

        {/* Chart */}
        <Card gap="xl" p="xl" w="full">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            Messages sent by day
          </Text>
          {isLoading ? (
            <Flex align="center" justify="center" py="8xl">
              <LoadingRedoAnimation />
            </Flex>
          ) : (
            <RedoLineAndBarChart
              chartStyle="bar"
              legendPosition="top"
              series={chartSeries}
              showTooltipSeriesNames
              valueAxisFormatter={(value: number) => value.toLocaleString()}
              valueTooltipFormatter={(value: number) => value.toLocaleString()}
            />
          )}
        </Card>
      </Flex>
    );
  },
);
